/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// MUI Components
import { TextField, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Libraries
import { z } from 'zod';

// Assets
import SigninImage from '../assets/Signin/Pool.webp';
import { useState } from 'react';
import { notifyAPIClient } from '../utils/toast';
import { apiPost } from '../utils/apiClient';
import { SignInResponse } from '../types/SignInPage.type';
import { useTranslation } from '../i18n';

const SignInInput = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(16, 'Password must be at most 16 characters long'),
});
type SignInInputType = z.infer<typeof SignInInput>;
type FormErrors = {
  email?: string;
  password?: string;
};

const getEnvVariable = (key: string): string => {
  const value = process.env[key];
  if (!value) {
    throw new Error(`Environment variable ${key} is not defined`);
  }
  return value;
};

const SignIn = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const handleConfirm = async (data: SignInInputType) => {
    try {
      const response = await apiPost<SignInResponse, SignInInputType>(
        '/auth/sign-in',
        data,
      );

      localStorage.setItem(
        'aquatics_hub_2025_tokens',
        JSON.stringify(response.data.tokens),
      );
      localStorage.setItem(
        'aquatics_hub_2025_user',
        JSON.stringify(response.data.serializedUser),
      );

      if (response.data.serializedUser.role === 'COACH')
        navigate('/assessment-home');
      else if (response.data.serializedUser.role === 'ADMIN') navigate('/home');
    } catch (error: any) {
      notifyAPIClient(error.response.data.message || 'An error occurred');
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = SignInInput.parse({
        email: formData.get('email'),
        password: formData.get('password'),
      });
      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      } else {
        console.error(error);
      }
    }
  };

  // ==============================
  // Render
  // ==============================
  return (
    <div className="flex h-screen">
      {/* Left Section */}
      <div className="relative w-1/2">
        <img
          src={SigninImage}
          alt="Sign in"
          className="h-full w-full object-cover"
        />
        <h1 className="absolute left-4 bottom-0 flex items-start justify-center py-4 text-xl text-white font-bold">
          AQUATICS HUB - Version {getEnvVariable('REACT_APP_HEAD_VERSION')}
        </h1>
      </div>
      {/* Right Section */}
      <div className="flex w-1/2 items-center justify-center">
        <div className="flex w-2/3 flex-col gap-4">
          {/* Title */}
          <h1 className="text-left text-4xl font-semibold text-primary">
            {t('signin.title')}
          </h1>
          {/* Subtitle / Signup link */}
          {/* <div className="flex flex-row gap-2">
            <h1>Don’t have an account yet?</h1>
            <h1
              className="font-bold text-primary hover:cursor-pointer"
              onClick={handleClick}
            >
              Signup
            </h1>
          </div> */}
          {/* Input Fields */}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="dense"
              id="email"
              name="email"
              label={t('signin.email')}
              type="text"
              fullWidth
              variant="standard"
              error={Boolean(formErrors.email)}
              helperText={formErrors.email}
            />
            <TextField
              margin="dense"
              id="password"
              name="password"
              label={t('signin.password')}
              type="password"
              fullWidth
              variant="standard"
              error={Boolean(formErrors.password)}
              helperText={formErrors.password}
            />

            <Box
              sx={{
                marginTop: '1rem',
              }}
            >
              <Button
                type="submit"
                variant="contained"
                className="!bg-primary !text-white w-full"
              >
                {t('signin.submit')}
              </Button>
            </Box>
          </form>

          {/* Remember & Forgot password */}
          {/* <div className="flex flex-row items-center justify-between gap-1">
            <div className="flex flex-row items-center gap-1">
              <Checkbox />
              <h1>Remember me</h1>
            </div>
            <h1 className="hover:cursor-pointer hover:text-primary">
              Forgot password?
            </h1>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
